import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { hasPermission } from '../utils/roles';
import { fmtDateBoth } from '../utils/dates';

const emptyForm = () => ({
  project_id: '', title: '', contract_number: '', start_date: '', end_date: '',
  total_amount: '', reminder_days_before: 7, notes: '', payments: [],
});
const emptyPayment = () => ({ label: '', amount: '', due_date: '', status: 'pending' });

export default function Contracts() {
  const { api, user } = useAuth();
  const toast = useToast();
  const canManage = hasPermission(user, 'contracts.manage') || ['admin', 'supervisor'].includes(user?.role);

  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = () => {
    const qs = projectFilter ? `?project_id=${projectFilter}` : '';
    setLoading(true);
    api(`/contracts${qs}`).then(setContracts).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [projectFilter]);
  useEffect(() => { api('/projects').then(setProjects).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setShowModal(true); };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      project_id: c.project_id || '', title: c.title || '', contract_number: c.contract_number || '',
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      end_date: c.end_date ? c.end_date.slice(0, 10) : '',
      total_amount: c.total_amount ?? '', reminder_days_before: c.reminder_days_before ?? 7,
      notes: c.notes || '',
      payments: (c.payments || []).map(p => ({
        id: p.id, label: p.label, amount: p.amount, status: p.status,
        due_date: p.due_date ? p.due_date.slice(0, 10) : '',
      })),
    });
    setShowModal(true);
  };

  const addPayment = () => setForm(f => ({ ...f, payments: [...f.payments, emptyPayment()] }));
  const updatePayment = (i, patch) => setForm(f => ({ ...f, payments: f.payments.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const removePayment = (i) => setForm(f => ({ ...f, payments: f.payments.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.project_id || !form.title.trim()) { toast.warning('حدّد المشروع وعنوان العقد'); return; }
    try {
      const payload = {
        ...form,
        total_amount: form.total_amount === '' ? null : Number(form.total_amount),
        reminder_days_before: Number(form.reminder_days_before) || 0,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        payments: form.payments
          .filter(p => p.label && p.due_date)
          .map(p => ({
            id: p.id, label: p.label, amount: Number(p.amount) || 0,
            due_date: p.due_date, status: p.status || 'pending',
          })),
      };
      if (editing) {
        await api(`/contracts/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('تم تحديث العقد');
      } else {
        await api('/contracts', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('تمت إضافة العقد');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (c) => {
    if (!window.confirm(`حذف العقد "${c.title}"؟`)) return;
    try {
      await api(`/contracts/${c.id}`, { method: 'DELETE' });
      toast.success('تم الحذف');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>العقود والدفعات</h2>
        {canManage && <button className="btn btn-primary" onClick={openNew}>+ عقد جديد</button>}
      </div>

      <div className="filters">
        <select className="form-select" value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
          <option value="">كل المشاريع</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>

      {loading ? <div className="loader">جاري التحميل...</div> : contracts.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">📄</div>لا توجد عقود</div></div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {contracts.map(c => {
            const paid = (c.payments || []).filter(p => p.status === 'paid').length;
            return (
              <div key={c.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.title}{c.contract_number && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — {c.contract_number}</span>}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      {c.project?.title && <span>📁 {c.project.title} • </span>}
                      {c.end_date && <span>ينتهي: {fmtDateBoth(c.end_date)}</span>}
                    </div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(c)}>تعديل</button>
                      <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)' }} onClick={() => remove(c)}>حذف</button>
                    </div>
                  )}
                </div>
                {(c.payments || []).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>الدفعات ({paid}/{c.payments.length} مدفوعة)</div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>الدفعة</th><th>المبلغ</th><th>الاستحقاق</th><th>الحالة</th></tr></thead>
                        <tbody>
                          {c.payments.map(p => (
                            <tr key={p.id}>
                              <td>{p.label}</td>
                              <td>{p.amount}</td>
                              <td>{fmtDateBoth(p.due_date)}</td>
                              <td><span className={`badge badge-${p.status === 'paid' ? 'completed' : 'pending'}`}>{p.status === 'paid' ? 'مدفوعة' : 'مستحقة'}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-title">{editing ? 'تعديل عقد' : 'عقد جديد'}</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المشروع *</label>
                <select className="form-select" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
                  <option value="">— اختر —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">رقم العقد</label>
                <input className="form-input" value={form.contract_number} onChange={e => setForm(f => ({ ...f, contract_number: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">عنوان العقد *</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">تاريخ البداية</label>
                <input className="form-input" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">تاريخ الانتهاء</label>
                <input className="form-input" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">القيمة الإجمالية</label>
                <input className="form-input" type="number" min="0" value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">تذكير قبل (أيام)</label>
                <input className="form-input" type="number" min="0" value={form.reminder_days_before} onChange={e => setForm(f => ({ ...f, reminder_days_before: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">ملاحظات</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label className="form-label" style={{ margin: 0 }}>الدفعات</label>
                <button className="btn btn-sm btn-outline" onClick={addPayment}>+ دفعة</button>
              </div>
              {form.payments.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>لا دفعات مضافة.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.payments.map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.4fr auto auto', gap: 6, alignItems: 'center' }}>
                    <input className="form-input" placeholder="الوصف" value={p.label} onChange={e => updatePayment(i, { label: e.target.value })} />
                    <input className="form-input" type="number" min="0" placeholder="المبلغ" value={p.amount} onChange={e => updatePayment(i, { amount: e.target.value })} />
                    <input className="form-input" type="date" value={p.due_date} onChange={e => updatePayment(i, { due_date: e.target.value })} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={p.status === 'paid'} onChange={e => updatePayment(i, { status: e.target.checked ? 'paid' : 'pending' })} />
                      مدفوعة
                    </label>
                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)' }} onClick={() => removePayment(i)}>×</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={save} disabled={!form.project_id || !form.title.trim()}>{editing ? 'حفظ' : 'إضافة'}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
